import { useCallback, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';
import { getText, TiptapOutputType } from '@amzn/awscat-editor';
import { resolveAltEntitiesValue } from '../../../editor/resolveAltEntitiesValue';

/** The Tiptap node name registered by the CustomImage extension. */
const IMAGE_NODE_NAME = 'image';

/** State and actions for editing an image's alt text with entity references. */
export interface AltTextEditing {
  /** Whether the alt-text editing modal is open. */
  readonly isAltTextModalVisible: boolean;
  /** Value to seed the modal with, resolved from the selected image. */
  readonly initialAltTextValue: TiptapOutputType;
  /** Opens the alt-text modal for the image currently selected in the editor. */
  readonly openAltTextModal: (editor: Editor) => void;
  /** Writes the edited alt text back onto the selected image and closes the modal. */
  readonly saveAltTextValue: (value: TiptapOutputType) => void;
  /** Closes the alt-text modal without saving. */
  readonly dismissAltTextModal: () => void;
}

/** Serializes the modal value into the `data-alt-entities` attribute form. */
function serializeAltEntities(value: TiptapOutputType): string | null {
  // A plain string clears the attribute when emptied.
  if (typeof value === 'string') return value || null;

  // A Tiptap doc with no visible text is a cleared alt; store null so it
  // matches the emptied-string case instead of persisting an empty-doc blob.
  if (!getText(value).trim()) return null;
  return JSON.stringify(value);
}

/**
 * Drives the image alt-text-with-entities modal for a v2 editor.
 *
 * The write-back runs a Tiptap command, so the edit flows out through the
 * editor's existing `onChange` and is persisted by the surrounding editor's
 * staging path — no manual document patching needed.
 */
export function useAltTextEditing(editor: Editor | undefined): AltTextEditing {
  const [isAltTextModalVisible, setModalVisible] = useState(false);
  const [initialAltTextValue, setInitialValue] = useState<TiptapOutputType>('');

  // The document position of the image being edited; read on save so the
  // command targets the right node even after the editor loses focus.
  const nodePosRef = useRef<number | undefined>(undefined);

  // When the editor's bubble menu requests alt-text editing, resolve the
  // selected image's stored value and open the modal.
  const openAltTextModal = useCallback((ed: Editor) => {
    const { from } = ed.state.selection;
    const node = ed.state.doc.nodeAt(from);
    if (!node || node.type.name !== IMAGE_NODE_NAME) return;

    nodePosRef.current = from;
    setInitialValue(resolveAltEntitiesValue(node.attrs['data-alt-entities'], node.attrs.alt));
    setModalVisible(true);
  }, []);

  // When the user saves, write the value onto the selected image; the resulting
  // document change propagates through the editor's onChange for persistence.
  const saveAltTextValue = useCallback(
    (value: TiptapOutputType) => {
      const pos = nodePosRef.current;
      if (editor && pos !== undefined) {
        editor
          .chain()
          .setNodeSelection(pos)
          .updateAttributes(IMAGE_NODE_NAME, { 'data-alt-entities': serializeAltEntities(value) })
          .run();
      }
      setModalVisible(false);
    },
    [editor],
  );

  const dismissAltTextModal = useCallback(() => setModalVisible(false), []);

  return {
    isAltTextModalVisible,
    initialAltTextValue,
    openAltTextModal,
    saveAltTextValue,
    dismissAltTextModal,
  };
}
